import React, {
  CSSProperties,
  memo,
  ReactElement,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import TabContext from '../Context/TabContext';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import ModalContext from '../Context/ModalContext';
import iconMixin from '../../styles/utils/iconMixin';

const activeTab = () => {
  return css`
    position: relative;
    left: 0;
    display: block;
    padding-bottom: 100px;
  `;
};

export const TabContainer: any = styled.div`
  position: relative;
  height: 100%;
  display: none;
  ${({ isActiveTab }: any) => (isActiveTab ? activeTab() : null)};
`;

const Icon = styled.div`
  ${iconMixin('48px')};
  color: #ffa000;
  padding: 10px 10px 0 0;
`;

type ITabContentProps<TabId = string> = {
  id: TabId;
  children: any;
  label?: string;
  isActiveTab?: boolean;
  activateTab?: any;
  onFocus?: any;
  shouldRender?: boolean; //Used by parent tab container - If in use, explicitly provide true/false (undefined will display the tab).
  order?: any; //Used by parent tab container, I think.
  badge?: number; //Used by parent tab container, I think.
  onlyRenderWhenActive?: boolean; //Used by parent tab container, I think.
  type?: string; //Used by parent tab container, I think.
  tabStyle?: CSSProperties;
  permissionReference?: string;
  tooltip?: ReactElement | string; //Display a tooltip with this content when hovering the mouse over the tab.
  tabContainer?: { className: string }; //Tab container slot.
  resetActiveTab?: (unexpectedTabId?: number) => void;
};

const TabContent = <TabId extends unknown>({
  id,
  isActiveTab = false,
  activateTab: activateTabProp,
  onFocus,
  children,
  shouldRender,
  tabContainer: tabContainerSlot,
  resetActiveTab,
}: ITabContentProps<TabId>) => {
  const { tabStyles } = useContext(ModalContext);
  const [activeOnce, setActiveOnce] = useState(false);

  useEffect(() => {
    if (isActiveTab && !activeOnce) {
      setActiveOnce(isActiveTab);
    }
  }, [activeOnce, isActiveTab]);

  const activateTab = useCallback(() => {
    if (!isActiveTab) {
      activateTabProp(id);
      onFocus?.();
    }
  }, [activateTabProp, id, isActiveTab, onFocus]);

  useEffect(() => {
    if (shouldRender === false && activeOnce) {
      resetActiveTab?.(id);
    }
  }, [shouldRender, activeOnce]);

  return shouldRender !== false ? (
    <TabContainer
      data-testid={`tab-container-${id}`}
      isActiveTab={isActiveTab}
      onFocus={activateTab}
      style={isActiveTab ? tabStyles?.container : undefined}
      className={tabContainerSlot?.className}
    >
      <TabContext.Provider value={{ tabId: id as string, isActiveTab: isActiveTab }}>
        <Suspense fallback={<ProgressIndicator />}>{activeOnce ? children : null}</Suspense>
      </TabContext.Provider>
    </TabContainer>
  ) : (
    <></>
  );
};

export default memo(TabContent, (prev, next) => prev.isActiveTab === next.isActiveTab && !next.isActiveTab);
